/**
 * Packaging Combine & Decomposition Engine
 * Cung cấp các thuật toán tính toán định mức in ghép (Gang-Run),
 * kiểm tra tính tương đồng (Homogeneity Guard), tính dôi dư (Surplus),
 * Ma trận bù hao bậc thang (Tiered Waste), Phân bổ giá thành theo Diện tích x UP,
 * và Kiểm soát hướng thớ giấy (Grain Direction Guard).
 */
import {
  calculateTieredOffsetWaste,
  calculateGangRunAreaWeights,
  validateGrainDirectionCompatibility,
  type TieredWasteResult,
  type GangRunAreaWeightItem,
} from "@apv-erp/status";

export {
  calculateTieredOffsetWaste,
  calculateGangRunAreaWeights,
  validateGrainDirectionCompatibility,
  type TieredWasteResult,
  type GangRunAreaWeightItem,
};

export interface PoolItem {
  id: string; // orderDetailId
  detailId?: string;
  orderId: string;
  orderCode: string;
  customerId: string;
  customerName: string;
  customerCode?: string;
  productId?: string | null;
  productCode?: string | null;
  productName: string;
  productImage?: string | null;
  unit?: string | null;
  quantity: number;
  plannedQty: number;
  remainingPlanQty: number;
  deliveryDeadline?: string | Date | null;
  prepressCode?: string | null;
  layoutCode?: string | null;
  printLayoutId?: string | null;
  layoutImage?: string | null;
  effectivePaperSize?: string | null;
  effectiveUps?: number | null;
  isValidForProduction: boolean;
  missingReason?: string | null;
  calculatedSheets?: number | null;
  materialId?: string | null;
  materialName?: string | null;
  materialCode?: string | null;
  materialSpecs?: string | null;
  cutFormat?: string | null;
  productSpecs?: any;
  productProcesses?: any[];
  length?: number | null;
  width?: number | null;
  flatLength?: number | null;
  flatWidth?: number | null;
}

export interface HomogeneityCheckResult {
  isHomogeneous: boolean;
  canCombine: boolean;
  warnings: string[];
  errors: string[];
  primaryMaterial: string | null;
  primaryPaperSize: string | null;
  materialCount: number;
  paperSizeCount: number;
}

export interface GangRunSheetsResult {
  maxRequiredSheets: number;
  totalRunSheetsNeeded: number;
  allowanceSheets: number;
  tieredWaste?: TieredWasteResult;
  areaWeights?: GangRunAreaWeightItem[];
  itemDetails: Array<{
    id: string;
    targetQty: number;
    ups: number;
    requiredSheets: number;
    costWeightPct?: number;
  }>;
}

export interface ProductSurplusStat {
  id: string;
  targetQty: number;
  ups: number;
  expectedTotalPieces: number;
  surplusPieces: number;
  surplusPercent: number;
}

export interface PlannedRoutingStep {
  stepIndex: number;
  stepCode: string;
  stepName: string;
  level: "SHEET" | "PIECE";
  targetQty: number;
  unit: string;
  productId?: string | null;
  productName?: string | null;
  notes?: string;
}

/**
 * 1. Homogeneity Guard:
 * Kiểm tra các mặt hàng được chọn có cùng chất liệu giấy, cùng màng cán, cùng khổ in và tương thích hướng thớ giấy hay không.
 */
export function checkHomogeneity(items: PoolItem[]): HomogeneityCheckResult {
  if (!items || items.length === 0) {
    return {
      isHomogeneous: true,
      canCombine: true,
      warnings: [],
      errors: [],
      primaryMaterial: null,
      primaryPaperSize: null,
      materialCount: 0,
      paperSizeCount: 0,
    };
  }

  const warnings: string[] = [];
  const errors: string[] = [];

  // 1. Kiểm tra chất liệu giấy & màng cán bề mặt (Bắt buộc cùng loại để chạy in/cán chung 1 mẻ)
  const materialMap = new Map<string, string>();
  const finishingSet = new Set<string>();
  for (const item of items) {
    if (item.materialId || item.materialName) {
      const matKey = item.materialId || item.materialName || "UNSPECIFIED";
      const matLabel = item.materialName || item.materialCode || "Chưa chọn vật tư";
      materialMap.set(matKey, matLabel);
    }
    const surfaceFinishing = (item as any).surfaceFinishing || (item as any).laminationType;
    if (surfaceFinishing && String(surfaceFinishing).trim() !== "" && String(surfaceFinishing).toUpperCase() !== "NONE") {
      finishingSet.add(String(surfaceFinishing).trim());
    }
  }

  const materialNames = Array.from(materialMap.values());
  if (materialMap.size > 1) {
    errors.push(
      `Không thể ghép bài vì các sản phẩm khác loại chất liệu giấy: ${materialNames.join(" vs ")}. Bắt buộc cùng mã giấy để in chung.`
    );
  }
  if (finishingSet.size > 1) {
    errors.push(
      `Vi phạm Bất biến 3 (Invariant 3): Các sản phẩm có khai báo công nghệ cán màng khác nhau: ${Array.from(finishingSet).join(" vs ")}. Tờ in phải cán màng đồng nhất (Mờ vs Bóng vs Không cán).`
    );
  }

  // 1b. Khuyến nghị tương thích hướng thớ giấy (Grain Direction Advisory - Non-blocking)
  const grainCheck = validateGrainDirectionCompatibility({
    sheetGrainDirection: "LONG",
    items: items.map((item) => {
      let parsedSpecs: any = {};
      try {
        if (typeof item.productSpecs === "object" && item.productSpecs) {
          parsedSpecs = item.productSpecs;
        } else if (typeof item.productSpecs === "string" && item.productSpecs.trim().startsWith("{")) {
          parsedSpecs = JSON.parse(item.productSpecs);
        }
      } catch {}
      return {
        id: item.id,
        productName: item.productName,
        requiredGrain: parsedSpecs.grainDirection || (item as any).grainDirection || "ANY",
        strictGrainDirection: Boolean(parsedSpecs.strictGrainDirection || (item as any).strictGrainDirection),
        isRotated90: Boolean((item as any).isRotated90),
      };
    }),
  });
  if (grainCheck.warnings && grainCheck.warnings.length > 0) {
    warnings.push(...grainCheck.warnings);
  }
  if (grainCheck.errors && grainCheck.errors.length > 0) {
    warnings.push(...grainCheck.errors);
  }

  // 2. Kiểm tra khổ in máy
  const paperSizeMap = new Map<string, string>();
  for (const item of items) {
    if (item.effectivePaperSize) {
      paperSizeMap.set(item.effectivePaperSize.trim(), item.effectivePaperSize.trim());
    }
  }

  const paperSizes = Array.from(paperSizeMap.values());
  if (paperSizeMap.size > 1) {
    warnings.push(
      `Các bài bình/sản phẩm có quy cách khổ in khác nhau (${paperSizes.join(" vs ")}). Hệ thống sẽ dùng khổ lớn nhất hoặc khổ chuẩn của bài bình.`
    );
  }

  // 3. Kiểm tra Gate 1: Có item nào thiếu khổ in / UPS không
  const invalidItems = items.filter((i) => !i.isValidForProduction);
  if (invalidItems.length > 0) {
    errors.push(
      `Có ${invalidItems.length} mặt hàng chưa đủ điều kiện sản xuất (${invalidItems.map((i) => i.productName).join(", ")}): thiếu khổ in hoặc số UP.`
    );
  }

  const canCombine = errors.length === 0;
  const isHomogeneous = errors.length === 0 && warnings.length === 0;

  return {
    isHomogeneous,
    canCombine,
    warnings,
    errors,
    primaryMaterial: materialNames[0] || null,
    primaryPaperSize: paperSizes[0] || items[0]?.effectivePaperSize || null,
    materialCount: materialMap.size,
    paperSizeCount: paperSizeMap.size,
  };
}

/**
 * 2. Calculate Gang-Run Sheets & Area-Weighted Cost Allocation:
 * Tính số tờ in lớn của bài ghép: S_run = max_i( ceil(Q_i / UPS_i) ) + S_waste(Tiered).
 * Tuyệt đối không cộng dồn bù hao giữa các chi tiết!
 */
export function calculateGangRunSheets(
  items: Array<{
    id: string;
    quantity: number;
    ups: number;
    lengthMm?: number | null;
    widthMm?: number | null;
  }>,
  baseAllowance?: number,
  wasteOptions?: { colorCount?: number; postPressStepCount?: number }
): GangRunSheetsResult {
  if (!items || items.length === 0) {
    return {
      maxRequiredSheets: 0,
      totalRunSheetsNeeded: 0,
      allowanceSheets: baseAllowance ?? 0,
      itemDetails: [],
    };
  }

  // calculateGangRunAreaWeights returns an array and reads flatLengthMm/flatWidthMm (FB-HFYNKW)
  const areaWeights = calculateGangRunAreaWeights(
    items.map((item) => ({
      id: item.id,
      ups: Math.max(1, item.ups || 1),
      flatLengthMm: item.lengthMm ?? 100,
      flatWidthMm: item.widthMm ?? 100,
    }))
  );

  const itemDetails = items.map((item) => {
    const validUps = Math.max(1, item.ups || 1);
    const requiredSheets = Math.ceil(item.quantity / validUps);
    const weightInfo = areaWeights.find((w) => w.id === item.id);
    return {
      id: item.id,
      targetQty: item.quantity,
      ups: validUps,
      requiredSheets,
      costWeightPct: weightInfo?.weightPercent ?? Number((100 / items.length).toFixed(2)),
    };
  });

  const maxRequiredSheets = Math.max(...itemDetails.map((i) => i.requiredSheets), 0);
  const tieredWaste = calculateTieredOffsetWaste({
    baseSheets: maxRequiredSheets,
    colorCount: wasteOptions?.colorCount ?? 4,
    postPressStepCount: wasteOptions?.postPressStepCount ?? 2,
  });

  const effectiveAllowance =
    baseAllowance !== undefined ? baseAllowance : tieredWaste.totalWasteSheets;
  const totalRunSheetsNeeded =
    maxRequiredSheets > 0 ? maxRequiredSheets + effectiveAllowance : 0;

  return {
    maxRequiredSheets,
    totalRunSheetsNeeded,
    allowanceSheets: effectiveAllowance,
    tieredWaste,
    areaWeights,
    itemDetails,
  };
}


/**
 * 3. Calculate Surplus per Product:
 * Tính sản lượng dôi dư của từng con khi in ghép:
 * Delta_i = (S_run * UPS_i) - Q_i
 */
export function calculateSurplusPerProduct(
  items: Array<{ id: string; quantity: number; ups: number }>,
  runSheets: number
): ProductSurplusStat[] {
  if (!items || items.length === 0 || runSheets <= 0) return [];

  return items.map((item) => {
    const validUps = Math.max(1, item.ups || 1);
    const expectedTotalPieces = runSheets * validUps;
    const surplusPieces = Math.max(0, expectedTotalPieces - item.quantity);
    const surplusPercent =
      item.quantity > 0 ? (surplusPieces / item.quantity) * 100 : 0;

    return {
      id: item.id,
      targetQty: item.quantity,
      ups: validUps,
      expectedTotalPieces,
      surplusPieces,
      surplusPercent: Math.round(surplusPercent * 10) / 10,
    };
  });
}

/**
 * 4. Separate Routing Steps:
 * Sheet-level (Cắt xén mộc, In Offset, Cán màng, Bế gân): Gộp 1 lần theo số tờ in lớn S_run.
 * Piece-level (Xé bế, Dán hộp, KCS, Đóng gói): Phân rã theo từng mã sản phẩm con theo số lượng Q_i.
 */
export function separateRoutingSteps(
  items: PoolItem[],
  runSheets: number
): PlannedRoutingStep[] {
  const steps: PlannedRoutingStep[] = [];
  let stepIndex = 1;

  // — TẦNG 1: CÔNG ĐOẠN TỜ IN LỚN (SHEET-LEVEL) —
  steps.push({
    stepIndex: stepIndex++,
    stepCode: "SHEET_CUT",
    stepName: "Cắt xén phôi giấy mộc",
    level: "SHEET",
    targetQty: runSheets,
    unit: "tờ lớn",
    notes: `Khổ xả mộc cho bài in (${items.length} mặt hàng ghép)`,
  });

  steps.push({
    stepIndex: stepIndex++,
    stepCode: "PRINT_OFFSET",
    stepName: "In Offset công nghiệp",
    level: "SHEET",
    targetQty: runSheets,
    unit: "tờ in",
    notes: `Bài in ${items.map((i) => i.productName).join(" + ")}`,
  });

  steps.push({
    stepIndex: stepIndex++,
    stepCode: "LAMINATE",
    stepName: "Cán màng bảo vệ",
    level: "SHEET",
    targetQty: runSheets,
    unit: "tờ in",
    notes: "Cán màng nhiệt mặt in",
  });

  steps.push({
    stepIndex: stepIndex++,
    stepCode: "DIE_CUT",
    stepName: "Bế gân định hình",
    level: "SHEET",
    targetQty: runSheets,
    unit: "tờ in",
    notes: "Khuôn bế ghép",
  });

  // — TẦNG 2: CÔNG ĐOẠN THÀNH PHẨM CON (PIECE-LEVEL) —
  for (const item of items) {
    const qty = item.remainingPlanQty || item.quantity;
    const unit = item.unit || "hộp";

    steps.push({
      stepIndex: stepIndex++,
      stepCode: "STRIP",
      stepName: `Bóc phế & tách con: ${item.productName}`,
      level: "PIECE",
      targetQty: qty,
      unit,
      productId: item.productId,
      productName: item.productName,
      notes: `Tách rời từ tờ bế (${item.effectiveUps || 1} UPS)`,
    });

    steps.push({
      stepIndex: stepIndex++,
      stepCode: "GLUE",
      stepName: `Dán đáy / dán cạnh: ${item.productName}`,
      level: "PIECE",
      targetQty: qty,
      unit,
      productId: item.productId,
      productName: item.productName,
      notes: "Dán hộp thành phẩm",
    });

    steps.push({
      stepIndex: stepIndex++,
      stepCode: "QC_PACK",
      stepName: `KCS & Đóng thùng: ${item.productName}`,
      level: "PIECE",
      targetQty: qty,
      unit,
      productId: item.productId,
      productName: item.productName,
      notes: `Đơn: ${item.orderCode} • KH: ${item.customerName}`,
    });
  }

  return steps;
}
